import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, LogIn, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import logo from '@/assets/images/logo.png';
import { estResponsableBoutique, estResponsableCF } from '@/utils/roleLabels';
import { ClavierTactile, gesteEstTactile } from '@/components/shared/ClavierTactile';

export const ClassicLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const navigate = useNavigate();

  const { login, isAuthenticated, loading } = useAuth();

  /**
   * Le clavier systeme s'ouvrait ici comme ailleurs, en plus du clavier
   * tactile de l'application : les deux a la fois, sur un ecran deja a
   * l'etroit (retour DG 15/10 — deja corrige pour la vendeuse via le pave
   * numerique, jamais ici).
   *
   * `readOnly` par defaut bloque le clavier systeme — c'est lui qui compte,
   * pas un clavier tactile absent ici au premier chargement. Au focus, si le
   * dernier geste n'etait pas tactile (souris, ou Tab au clavier physique),
   * on l'ouvre aussitot : sans clavier systeme a bloquer sur un poste de
   * bureau, readOnly n'y servirait qu'a empecher de taper.
   */
  const surFocusChampConnexion = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!gesteEstTactile()) e.currentTarget.readOnly = false;
  };
  const surQuitteChampConnexion = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.readOnly = true;
  };

  // Fonction pour déterminer le dashboard selon les rôles
  const getDashboardRoute = (roles: any[]) => {
    const roleNames = roles.map((role: any) => {
      if (typeof role === 'string') return role;
      if (role && typeof role === 'object') return role.name || role;
      return String(role);
    });

    if (roleNames.includes('dg') || roleNames.includes('director_general') || roleNames.includes('directeur_general')) {
      return '/dg/dashboard';
    }
    if (roleNames.includes('accountant') || roleNames.includes('comptable')) {
      return '/accountant/dashboard';
    }
    if (roleNames.includes('driver') || roleNames.includes('livreur') || roleNames.includes('delivery_driver')) {
      return '/driver/journee';
    }
    if (roleNames.includes('shop_seller') || roleNames.includes('vendeur') || roleNames.includes('seller')) {
      return '/seller/pos';
    }
    if (roleNames.includes('support') || roleNames.includes('support_tech')) {
      return '/support/dashboard';
    }
    if (roleNames.includes('farm_agent') || roleNames.includes('agent_ferme') || roleNames.includes('farm_manager') || roleNames.includes('responsable_ferme')) {
      return '/farm/dashboard';
    }
    // Inclut resp_multi_boutique : sans lui, un responsable multi-boutiques
    // etait renvoye au tableau de bord administrateur.
    if (roleNames.some(estResponsableBoutique)) {
      return '/store/dashboard';
    }
    if (roleNames.some(estResponsableCF)) {
      return '/cold-storage/dashboard';
    }
    if (roleNames.includes('admin') || roleNames.includes('administrateur')) {
      return '/admin/dashboard';
    }
    return '/admin/dashboard';
  };

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated) {
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          const roles = userData?.roles || [];
          const redirectPath = getDashboardRoute(roles);
          navigate(redirectPath, { replace: true });
        } catch (e) {
          navigate('/admin/dashboard', { replace: true });
        }
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await login({ email, password, twoFactorCode: requiresTwoFactor ? twoFactorCode : undefined });

      // Check if 2FA is required
      if (response?.data?.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setSubmitting(false);
        return;
      }

      const userData = response?.data?.user || response?.data;
      const roles = (userData as any)?.roles || [];
      const redirectPath = getDashboardRoute(roles);

      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Email ou mot de passe incorrect');
      setSubmitting(false);
    }
  };

  // Écran de chargement initial seulement
  if (loading) {
    return (
      <div className="min-h-screen bg-primary-600 flex flex-col items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl mb-8">
          <img src={logo} alt="Volaille d'Or" className="h-24 w-24 object-contain" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Volaille d'Or</h2>
        <div className="flex items-center gap-3 text-primary-100">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    // Fragment, pas un seul div : le clavier doit rester HORS de la ligne flex
    // ci-dessous. A l'interieur, il devenait un troisieme panneau au lieu de
    // pousser la page vers le haut — sa place reservee (ClavierTactile.tsx)
    // ne vaut que pour un frere en flux normal, pas pour un element de plus
    // dans un "flex" en ligne.
    <>
      <div className="min-h-screen bg-neutral-50 flex">
      {/* Panneau gauche - Decoratif */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-700 rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-primary-400 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-30" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-white">
          <div className="bg-white rounded-3xl p-6 shadow-elevated mb-8">
            <img src={logo} alt="Volaille d'Or" className="h-32 w-32 object-contain" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-center">Volaille d'Or</h1>
          <p className="text-primary-100 text-lg text-center max-w-md">
            Plateforme de gestion integree pour la production et distribution de volailles
          </p>
          <div className="mt-12 grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold">100%</div>
              <div className="text-primary-200 text-sm mt-1">Tracabilite</div>
            </div>
            <div>
              <div className="text-3xl font-bold">24/7</div>
              <div className="text-primary-200 text-sm mt-1">Disponible</div>
            </div>
            <div>
              <div className="text-3xl font-bold">Multi</div>
              <div className="text-primary-200 text-sm mt-1">Sites</div>
            </div>
          </div>
        </div>
      </div>

      {/* Panneau droit - Formulaire */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center bg-white rounded-2xl p-4 shadow-card border border-neutral-200 mb-4">
              <img src={logo} alt="Volaille d'Or" className="h-20 w-20 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Volaille d'Or</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-card border border-neutral-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-neutral-900">Connexion</h2>
              <p className="text-neutral-500 mt-2">Accedez a votre espace de gestion</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="form-label">
                  Adresse email
                </label>
                <input
                  id="email"
                  // "text" et non "email" : un champ "email" refuse de dire
                  // ou est son curseur (Chrome leve une exception rien qu'a
                  // le lire), et le clavier tactile ne peut alors jamais
                  // corriger une lettre au milieu — seulement a la fin.
                  // inputMode="email" garde le "@" a portee sur un clavier
                  // natif, sans ce blocage.
                  type="text"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="votre@email.com"
                  required
                  autoComplete="email"
                  disabled={submitting}
                  readOnly
                  data-clavier-force="oui"
                  onFocus={surFocusChampConnexion}
                  onBlur={surQuitteChampConnexion}
                />
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input pr-12"
                    placeholder="Votre mot de passe"
                    required
                    autoComplete="current-password"
                    disabled={submitting}
                    readOnly
                    data-clavier-force="oui"
                    onFocus={surFocusChampConnexion}
                    onBlur={surQuitteChampConnexion}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {requiresTwoFactor && (
                <div>
                  <div className="flex items-center gap-2 p-3 bg-primary-50 border border-primary-200 rounded-lg text-primary-700 text-sm mb-4">
                    <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                    <span>Entrez le code de votre application d'authentification</span>
                  </div>
                  <label htmlFor="twoFactorCode" className="form-label">
                    Code 2FA
                  </label>
                  <input
                    id="twoFactorCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                    className="form-input text-center text-2xl tracking-widest"
                    placeholder="000000"
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    disabled={submitting}
                    readOnly
                    data-clavier-force="oui"
                    onFocus={surFocusChampConnexion}
                    onBlur={surQuitteChampConnexion}
                  />
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary btn-lg w-full"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Chargement en cours...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Se connecter</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Mot de passe oublie ?
              </button>
              <div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
                >
                  ← Retour à l'écran tactile
                </button>
              </div>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-neutral-500">
            Une qualite de chez nous
          </p>
        </div>
      </div>

    </div>

      {/* Le clavier de la caisse commence ici.
          Le poste est tactile et le systeme n'ouvre aucun clavier : sans
          celui-ci, la vendeuse ne pouvait meme pas taper son mot de passe
          pour entrer. Hors du flex ci-dessus : un frere en flux normal, dont
          la place reservee pousse vraiment la page, plutot qu'un panneau de
          plus dans la ligne. */}
      <ClavierTactile />
    </>
  );
};
